import { Loader } from '@ur-solutions/react-components'
import type { LucideIcon } from 'lucide-react'
import React, { useCallback } from 'react'
import { NavLink } from 'react-router'
import styled, { keyframes } from 'styled-components'

const growBackground = keyframes`
  from {
    top: 31px;
    left: 31px;
    width: 0%;
    height: 0%;
  }
  to {
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
  }
`

const Badge = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 28px;
  padding: 0 16px;
  min-width: 54px;

  border-radius: 999px;
  font-size: 0.9em;
  color: ${props => props.theme.colors.gray2};
  background-color: ${props => props.theme.colors.tertiary}55;
`

const Item = styled(NavLink)`
  position: relative;
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 3.875rem;
  padding: 0 16px;

  color: ${props => props.theme.colors.gray4};
  border-radius: 16px;

  & > div {
    display: flex;
    align-items: center;
    gap: 1rem;
  }

  & > div > svg {
    flex-shrink: 0;
    width: 24px;
    height: 24px;
  }

  &::before {
    position: absolute;
    z-index: -1;
    top: 0;
    left: 0;
    width: 0;
    height: 0;

    background-color: ${props => props.theme.colors.primary};
    border-radius: 16px;
  }

  &:hover {
    text-decoration: none;

    span {
      font-weight: 600;
    }
    & > div > svg {
      color: black;
    }
  }

  &.active {
    &::before {
      content: '';
      animation: ${growBackground} 200ms ease-out forwards;
    }

    & > div > svg {
      color: white;
    }
    span {
      color: white;
      font-weight: 600;
    }

    ${Badge} {
      color: white;
      font-weight: 600;
      background-color: ${props => props.theme.colors.tertiary}aa;
    }
  }
`

export interface SidebarNavItemProps {
  className?: string

  to: string
  end?: boolean
  target?: string
  text: string
  badge?: string | number
  badgeLoading?: boolean

  icon: LucideIcon

  onBeforeNavigate?(): void
}

export const SidebarNavItem: React.FC<SidebarNavItemProps> = ({
  className,

  to,
  target,
  text,
  badge,
  badgeLoading = false,

  icon: Icon,

  end,

  onBeforeNavigate,
}) => {
  const handleClick = useCallback(
    (evt: React.MouseEvent) => {
      onBeforeNavigate?.()
      if (!to.startsWith('http')) return

      evt.preventDefault()
      if (target !== '_blank') window.location.href = to
      else window.open(to, target)
    },
    [onBeforeNavigate, target, to]
  )

  return (
    <Item
      className={className}
      to={to}
      end={end}
      target={target}
      onClick={handleClick}
    >
      <div>
        <Icon size={24} strokeWidth={1.5} aria-hidden="true" />
        <span>{text}</span>
      </div>

      {typeof badge !== 'undefined' && (
        <Badge>{badgeLoading ? <Loader.Dots size={26} /> : badge}</Badge>
      )}
    </Item>
  )
}
